import {
  Avatar,
  Badge,
  BottomNavigationActionProps,
  Box,
  Chip,
  Skeleton,
  Stack,
  styled,
  useTheme,
} from '@mui/material'
import { MouseEventHandler } from 'react'
import { Icons } from 'utoniq-ui'

import { useBrowserLink } from '@/components/lib/BrowserLink'
import { config } from '@/config'
import { useCurrentUser, useRouter, useTranslation } from '@/core'
import { usePrivateAction } from '@/lib/privateAction'
import { ArtistTabKeys } from '@/types/tabs'

export type TabList = {
  [key in ArtistTabKeys[number]]: {
    label: BottomNavigationActionProps['label']
    icon: BottomNavigationActionProps['icon']
    value: BottomNavigationActionProps['value']
    href?: string
    color?: string
    onClick?: MouseEventHandler<HTMLAnchorElement | HTMLButtonElement>
  }
}

type UseTabListProps = {
  avatarUri?: string
  appLink?: string
  isLive?: boolean
  /** 配送申請・コンビニ支払いなどが必要な状態であればtrue */
  hasManagementTask?: boolean
  /** 未読の通知数 */
  unreadNotificationCount?: number
  totalUnreadMentionCount?: number
}

export const useTabList = ({
  avatarUri,
  appLink,
  isLive,
  unreadNotificationCount = 0,
  totalUnreadMentionCount = 0,
  hasManagementTask = false,
}: UseTabListProps): TabList => {
  const { t } = useTranslation()
  const { user: currentUser } = useCurrentUser()
  const { handleLoginModal } = usePrivateAction()
  const { routerPages, generatePath, router } = useRouter<{
    artistId: string
  }>()
  const artistId = router.query.artistId || config.artistId
  const { onClick } = useBrowserLink(appLink ?? '', {
    presentationStyle: 'popover',
  })

  /**
   * @todo
   * https://app.clickup.com/t/908127/UT-8948
   * お知らせ未読分のバッジ表示データ組み込み
   */
  const hasNotificationBadge = false

  const theme = useTheme()

  return {
    home: {
      label: theme?.palette?.custom?.artistNavigation?.home ? t('home') : null,
      icon: theme?.palette?.custom?.artistNavigation?.home ? (
        <>
          <Icons.Home
            sx={{
              color:
                theme?.palette?.custom?.artistNavigation?.home || 'inherit',
            }}
          />
          {isLive && <ChipStyled label="LIVE" sx={{ mb: 2 }} />}
        </>
      ) : !avatarUri ? (
        <Skeleton variant="circular" width={40} height={40} />
      ) : (
        <Stack sx={{ position: 'relative' }}>
          <Avatar
            src={avatarUri}
            sx={{
              border: theme =>
                isLive ? `1px solid ${theme?.palette?.like?.tap}` : 'none',
            }}
          />
          {isLive && <ChipStyled label="LIVE" />}
        </Stack>
      ),
      value: 'home',
      href: generatePath(routerPages.ArtistTop, { artistId }),
      color: theme?.palette?.custom?.artistNavigation?.home,
    },
    collections: {
      label: t('collection'),
      icon: (
        <Icons.Collection
          sx={{
            color:
              theme?.palette?.custom?.artistNavigation?.collections ||
              'inherit',
          }}
        />
      ),
      value: 'collections',
      href: generatePath(routerPages.ArtistCollections, { artistId }),
      color: theme?.palette?.custom?.artistNavigation?.collections,
    },
    myCollections: {
      label: t('collection'),
      icon: (
        <Icons.Collection
          sx={{
            color:
              theme?.palette?.custom?.artistNavigation?.myCollections ||
              'inherit',
          }}
        />
      ),
      value: 'myCollections',
      href: generatePath(routerPages.ArtistMyCollections, { artistId }),
      onClick: handleLoginModal(),
      color: theme?.palette?.custom?.artistNavigation?.myCollections,
    },
    gachas: {
      label: t('gacha'),
      icon: (
        <Icons.Gacha
          sx={{
            color:
              theme?.palette?.custom?.artistNavigation?.gachas || 'inherit',
          }}
        />
      ),
      value: 'gachas',
      href: generatePath(routerPages.ArtistGachaDetailLatest, {
        artistId,
      }),
      color: theme?.palette?.custom?.artistNavigation?.gachas,
    },
    missions: {
      label: t('mission'),
      icon: (
        <Icons.Mission
          sx={{
            color:
              theme?.palette?.custom?.artistNavigation?.missions || 'inherit',
          }}
        />
      ),
      value: 'missions',
      href: generatePath(routerPages.ArtistMissions, { artistId }),
      color: theme?.palette?.custom?.artistNavigation?.missions,
    },
    trade: {
      label: t('trade'),
      icon: (
        <Icons.Market
          sx={{
            color: theme?.palette?.custom?.artistNavigation?.trade || 'inherit',
          }}
        />
      ),
      value: 'trade',
      href: generatePath(routerPages.ArtistTrade, { artistId }),
      color: theme?.palette?.custom?.artistNavigation?.trade,
    },
    cart: {
      label: t('cart'),
      icon: (
        <Icons.Store
          sx={{
            color: theme?.palette?.custom?.artistNavigation?.cart || 'inherit',
          }}
        />
      ),
      value: 'cart',
      href: routerPages.StoreCartList,
      // カートには匿名ログイン状態でもアクセスできる
      onClick: event => (currentUser ? () => {} : handleLoginModal()(event)),
      color: theme?.palette?.custom?.artistNavigation?.cart,
    },
    notifications: {
      label: t('notification'),
      icon: (
        <Box sx={{ display: 'inline-flex', position: 'relative' }}>
          <StyledBadge
            badgeContent={
              hasNotificationBadge && unreadNotificationCount ? '' : undefined
            }
            color={'like' as any}
          />
          <Icons.Notice
            sx={{
              color:
                theme?.palette?.custom?.artistNavigation?.notifications ||
                'inherit',
            }}
          />
        </Box>
      ),
      value: 'notifications',
      href: routerPages.Notifications,
      onClick: handleLoginModal(),
      color: theme?.palette?.custom?.artistNavigation?.notifications,
    },
    myProfile: {
      label: t('myProfile'),
      icon: (
        <Box sx={{ display: 'inline-flex', position: 'relative' }}>
          <StyledBadge
            badgeContent={hasManagementTask ? '' : undefined}
            color={'like' as any}
          />
          <Icons.MyPage
            sx={{
              color:
                theme?.palette?.custom?.artistNavigation?.myProfile ||
                'inherit',
            }}
          />
        </Box>
      ),
      value: 'myProfile',
      href: routerPages.MyProfile,
      onClick: handleLoginModal(),
      color: theme?.palette?.custom?.artistNavigation?.myProfile,
    },
    posts: {
      label: t('post'),
      icon: (
        <Icons.Post
          sx={{
            color: theme?.palette?.custom?.artistNavigation?.posts || 'inherit',
          }}
        />
      ),
      value: 'posts',
      href: generatePath(routerPages.ArtistPosts, { artistId }),
      color: theme?.palette?.custom?.artistNavigation?.posts,
    },
    threads: {
      label: t('community'),
      icon: (
        <Box sx={{ display: 'inline-flex', position: 'relative' }}>
          <StyledBadge
            badgeContent={totalUnreadMentionCount}
            color={'like' as any}
            sx={{
              '& > span': {
                fontWeight: 'bold',
                fontSize: 9,
                minWidth: 20,
                height: 20,
              },
            }}
          />
          <Icons.Thread
            sx={{
              color:
                theme?.palette?.custom?.artistNavigation?.threads || 'inherit',
            }}
          />
        </Box>
      ),
      value: 'threads',
      href: generatePath(routerPages.ArtistThreads, { artistId }),
      color: theme?.palette?.custom?.artistNavigation?.threads,
    },
    store: {
      label: t('store'),
      icon: (
        <Icons.Artist
          sx={{
            color: theme?.palette?.custom?.artistNavigation?.store || 'inherit',
          }}
        />
      ),
      value: 'store',
      href: generatePath(routerPages.ArtistStore, { artistId }),
      color: theme?.palette?.custom?.artistNavigation?.store,
    },
    ownedItems: {
      label: t('ownedItems'),
      icon: (
        <Icons.OwnedItems
          sx={{
            color:
              theme?.palette?.custom?.artistNavigation?.ownedItems || 'inherit',
          }}
        />
      ),
      value: 'ownedItems',
      href: generatePath(routerPages.MyOwnedItems),
      onClick: handleLoginModal(),
      color: theme?.palette?.custom?.artistNavigation?.ownedItems,
    },
    events: {
      label: t('event'),
      icon: (
        <Icons.ShokufesEvent
          sx={{
            color:
              theme?.palette?.custom?.artistNavigation?.events || 'inherit',
          }}
        />
      ),
      value: 'events',
      href: generatePath(routerPages.ArtistEvents, { artistId }),
      color: theme?.palette?.custom?.artistNavigation?.events,
    },
    postVideos: {
      label: t('postVideos'),
      icon: (
        <Icons.AudioPlay
          color="inherit"
          sx={{ height: '20px', width: '20px', my: '2px' }}
        />
      ),
      value: 'postVideos',
      href: generatePath(routerPages.ArtistPostVideos, { artistId }),
      color: theme?.palette?.custom?.artistNavigation?.postVideos,
    },
    gallery: {
      label: t('gallery'),
      icon: <Icons.Gallery color="inherit" />,
      value: 'gallery',
      href: generatePath(routerPages.ArtistGallery, { artistId }),
      color: theme?.palette?.custom?.artistNavigation?.gallery,
    },
    artistProfile: {
      label: t('artistProfile'),
      icon: <Icons.MyPage color="inherit" />,
      value: 'artistProfile',
      onClick: () => onClick(),
      color: theme?.palette?.custom?.artistNavigation?.artistProfile,
    },
  } as const
}

const ChipStyled = styled(Chip)(({ theme }) => ({
  color: 'white',
  backgroundColor: theme.palette.like.tap,
  position: 'absolute',
  bottom: 0,
  height: 'auto',
  width: 'auto',
  padding: 2,
  borderRadius: 2,
  left: '50%',
  transform: 'translateX(-50%)',
  '& .MuiChip-label': {
    paddingLeft: 4,
    paddingRight: 4,
  },
}))

const StyledBadge = styled(Badge)(() => ({
  position: 'absolute',
  color: 'white',
  top: 4,
  right: -2,
  width: 12,
  height: 12,
  '& > span': {
    fontWeight: 'bold',
    fontSize: 9,
    minWidth: 12,
    height: 12,
  },
}))
